import React from "react";
import { Box, List, ListItem, ListItemButton, ListItemText, Typography } from "@mui/material";
import mapboxgl from "mapbox-gl";

import { getOriginalCable } from "fond/draw/modes/planner/dropAssignments/helper";
import MapPopup from "fond/map/MapPopup";

import { AssignmentCableFeature, CableFeature } from "./types";

type Selection = { cable: CableFeature | AssignmentCableFeature };

interface SelectionPopupArgs {
  cables: (CableFeature | AssignmentCableFeature)[];

  lngLat: mapboxgl.LngLatLike;
  onSelect: (selection: Selection) => void;
  onClose: () => void;
}

/**
 * Popup component for selecting from multiple overlapping cables.
 * Displays a list of cables with their sink group information and source color indicator.
 */
const SelectionPopup: React.FC<SelectionPopupArgs> = ({ cables, lngLat, onSelect, onClose }) => {
  const getCableDisplayInfo = (cable: CableFeature | AssignmentCableFeature) => {
    const { sinkGroup } = getOriginalCable(cable).properties;
    const sourceColor = cable.properties.source.properties.color;
    const { serviceLocationIds } = sinkGroup.properties;

    return {
      sourceColor,
      serviceLocationIds: serviceLocationIds.toSorted(),
    };
  };

  return (
    <MapPopup lngLat={lngLat} onClose={onClose} title="Select Cable">
      <Box sx={{ maxHeight: 300, overflow: "auto", minWidth: 250 }}>
        <List dense disablePadding>
          {cables.map((cable) => {
            const originalCable = getOriginalCable(cable);
            const { sourceColor, serviceLocationIds } = getCableDisplayInfo(cable);
            return (
              <ListItem key={`cable-${cable.id}`} disablePadding>
                <ListItemButton onClick={() => onSelect({ cable })}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      mr: 1.5,
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: sourceColor,
                        border: "1px solid rgba(0,0,0,0.2)",
                        marginX: "auto",
                        marginY: "auto",
                      }}
                    />
                  </Box>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500}>
                        {originalCable.properties.fondId}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Service Locations: {serviceLocationIds.join(", ")}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </MapPopup>
  );
};

export default SelectionPopup;
